import { tokenPairSymbolSchema } from "../../orderbook";
import { ReferralSupportedExchangeIdSchema } from "../../shared/campaign-types";
import { ArenaEntryTierSchema } from "../cede-arena/entry-tier";
import { ArenaRewardKindSchema } from "../cede-arena/external-reward";
import { SOCIAL_NETWORK } from "../hub";
import { PaperTradingConfigReadSchema } from "../paper-trading/paper-trading-config";
import { PublicationStatusSchema } from "../solo-cms/publication-status";
import { CampaignVolumeDataSchema, CampaignsStatsResponseSchema } from "../stats";
import { TradingRuleSchema } from "./trade-to-earn-rules";
import Decimal from "decimal.js";
import z from "zod";

/**
 * Market type for campaign epochs.
 * Used to specify whether trades should be on spot or perpetual markets.
 */
export const CampaignMarketType = {
  SPOT: "spot",
  PERPS: "perps",
} as const;
export type CampaignMarketType = (typeof CampaignMarketType)[keyof typeof CampaignMarketType];
export const CampaignMarketTypeSchema = z.enum(["spot", "perps"]);

/**
 * Trading mode for campaign.
 * Controls which trading methods are available and eligible for rewards.
 * - `manual_only`: Only manual trading widget shown. Autopilot API blocked.
 * - `both`: Both manual and autopilot available. Both eligible for rewards.
 * - `streak_only`: Only autopilot shown. Manual trades processed but NOT eligible for rewards.
 */
export const CampaignTradingMode = {
  MANUAL_ONLY: "manual_only",
  BOTH: "both",
  STREAK_ONLY: "streak_only",
} as const;
export type CampaignTradingMode = (typeof CampaignTradingMode)[keyof typeof CampaignTradingMode];
export const CampaignTradingModeSchema = z.enum(["manual_only", "both", "streak_only"]);

// =============================================================================
// AUTOPILOT CONFIG SCHEMAS
// =============================================================================

/**
 * Autopilot config version 1 - matches AutomaticStreakConfig structure.
 * Used for backward compatibility with existing autopilot configurations.
 */
export const CampaignAutopilotConfigV1DataSchema = z.object({
  enabled: z.boolean(), // Replaces GrowthBook feature flags
  minTrades: z.number(),
  maxTrades: z.number(),
  minVolumePerTrade: z.number(),
  maxVolumePerTrade: z.number(),
  minVolumeDifference: z.number(), // Minimum difference between volumeMin and volumeMax
  validIntervals: z.array(z.number()),
  defaultStartDelayMinutes: z.number(), // Default delay in minutes before autopilot starts (0 = instant)
  defaultCycles: z.number(), // Default number of cycles shown in form
  volumeVariancePercent: z.number(), // Variance percentage for single volume input (±25% by default)
  defaultVolumePerTrade: z.number(), // Default volume shown in autopilot form
});
export type CampaignAutopilotConfigV1Data = z.infer<typeof CampaignAutopilotConfigV1DataSchema>;

/**
 * Bounds for the autopilot cadence-grid pitch (seconds between consecutive shared grid lines).
 * Single source of truth: the DTO schema, the streak-creation command, and the CMS form all derive
 * their default/min/max from these, so the legacy default can never drift between layers.
 *
 * The grid pitch controls how densely trades are spread across ALL autopilots and is intentionally
 * decoupled from the per-cycle safeguard: it may be well below 60s (a single autopilot still keeps
 * ≥ CYCLE_SAFE_MARGIN_MS between its OWN cycles, enforced in the AutomaticStreak entity), so different
 * autopilots can trade <60s apart. Minimum 1s; default 1s (max follows min → uniform 1s grid).
 */
export const MIN_TRADE_INTERVAL_SECONDS = 1;
export const MAX_TRADE_INTERVAL_SECONDS = 3600;
export const DEFAULT_MIN_TRADE_INTERVAL_SECONDS = 1;

/**
 * Bounds for the per-campaign buy→sell hold override (seconds a cycle keeps the bought position before
 * selling). Absent → legacy behavior (1–20s hold). Capped at 4 min so a hold cycle (start delay + hold +
 * latency) stays within the existing ~290s own-cycle spacing (CYCLE_SAFE_MARGIN_MS in the api) — no
 * scheduling changes needed.
 *
 * Splitting and holding are per-campaign controls but a campaign may enable only ONE (see the config
 * validation): a hold campaign places single orders. Combining them (a split+hold cycle) would exceed the
 * cycle-spacing margin and is intentionally out of scope — see the PR description for the tradeoff.
 */
export const MIN_HOLD_SECONDS = 1;
export const MAX_HOLD_SECONDS = 240;

/**
 * Profit (USD) per $1k of volume a user keeps after spread + taker fee, used as the margin in
 * the epoch volume target: `target = perEpochReward / (costPer1k + this) × 1000`.
 */
export const DEFAULT_ACCEPTABLE_PROFIT_USD = 0.1;

/**
 * Autopilot config version 2 - simplified with per-epoch volume targets.
 * minTargetVolume and maxTargetVolume are per-epoch totals.
 */
export const CampaignAutopilotConfigV2DataSchema = z
  .object({
    minTradeSize: z.number(),
    maxTradeSize: z.number(),
    minTargetVolume: z.number(), // per-epoch total
    maxTargetVolume: z.number(), // per-epoch total
    /** Maximum number of active autopilots (PENDING + IN_PROGRESS) allowed per epoch. If not set, no limit applies. */
    maxAutopilotsPerEpoch: z.number().optional(),
    /** Hidden reserved pool of autopilot spots for users who have never participated in any campaign. Consumed per (campaign, epoch), no refund. */
    reservedSpotsForNewUsers: z.number().int().min(0).default(2),
    /** Maximum number of active (PENDING + IN_PROGRESS) autopilots allowed per user per campaign. Default: 1. */
    numberOfAllowedActiveAutopilotPerUser: z.number().int().min(1).default(1),
    /**
     * When true, opting into autopilot skips the queue entirely: submitting the form
     * creates a streak immediately via CreateStreakCommand instead of joining the
     * picker queue. Optional + defaults to false so existing campaigns are unaffected.
     */
    disableQueueSystem: z.boolean().optional(),
    /**
     * Share (0–100) of the regular free slots the picker fills FIRST with users who
     * participated in the immediately preceding epoch, before filling the rest at
     * random. Absent / 0 = today's pure-random pick.
     */
    recurrencePercentage: z.number().int().min(0).max(100).optional(),
    /**
     * Bounds (seconds) on the gap between two consecutive lines of the shared cadence grid. The grid gaps are
     * randomized in [min, max] (identical for every autopilot in a campaign+epoch), and every autopilot snaps
     * its cycles onto those shared lines. When max is omitted it defaults to min → a uniform grid at the min
     * pitch. These bounds govern the SHARED grid density only (can be <60s → different autopilots trade
     * closer together); each autopilot independently keeps ≥ CYCLE_SAFE_MARGIN_MS between its own cycles.
     */
    minTradeIntervalSeconds: z
      .number()
      .int()
      .min(MIN_TRADE_INTERVAL_SECONDS)
      .max(MAX_TRADE_INTERVAL_SECONDS)
      .default(DEFAULT_MIN_TRADE_INTERVAL_SECONDS),
    maxTradeIntervalSeconds: z
      .number()
      .int()
      .min(MIN_TRADE_INTERVAL_SECONDS)
      .max(MAX_TRADE_INTERVAL_SECONDS)
      .optional(),
    /**
     * Per-campaign switch for multi-leg order splitting. Absent → fall back to the global
     * `automatic_streak:api:order_splitting_enable` flag (preserves current behavior for campaigns that
     * predate this field). Set explicitly to override per campaign. Mutually exclusive with a hold.
     */
    enableOrderSplitting: z.boolean().optional(),
    /**
     * Bounds (seconds) on the randomized buy→sell hold inside one cycle. Drawn uniformly in [min, max] per
     * cycle. When max is omitted it defaults to min; when both are omitted the legacy 1–20s hold applies.
     * A hold campaign does NOT split (mutually exclusive — see validation below).
     */
    minHoldSeconds: z.number().int().min(MIN_HOLD_SECONDS).max(MAX_HOLD_SECONDS).optional(),
    maxHoldSeconds: z.number().int().min(MIN_HOLD_SECONDS).max(MAX_HOLD_SECONDS).optional(),
    /**
     * Recompute this campaign's epoch volume target from the rolling average spread before each
     * epoch. Absent counts as OFF, so every campaign that predates the feature keeps the target
     * an admin set by hand until ops turns it on; the CMS seeds it ON for new campaigns.
     */
    dynamicVolumeTargetEnabled: z.boolean().optional(),
    /**
     * Epoch-wide volume BASELINE (USD): the target never goes below it. The recomputation can
     * only raise the epoch above this floor when the spread is tight enough, and falls back to
     * it both when the computed target prices lower and when it can't be computed at all
     * (no/stale 12h average, taker fee not configured, no pair). Absent, or under $1 (too small
     * to fund a single autopilot), → no floor, and the halt paths leave whatever an admin set by
     * hand. Only read by the pre-epoch recomputation.
     */
    defaultEpochVolumeTarget: z.number().positive().optional(),
    /**
     * Profit (USD) a user should keep per $1k of volume, on top of spread + taker fee.
     * Absent → `DEFAULT_ACCEPTABLE_PROFIT_USD`, resolved where the target is computed.
     */
    acceptableProfitUsd: z.number().min(0).optional(),
    /**
     * Taker fee (percent) for this campaign's venue, overriding the per-exchange table. Set it
     * where the table can't know the real rate: a tiered venue's per-pair class (KuCoin A 0.1% /
     * B 0.2% / C 0.3%, where the table assumes the worst), or a negotiated rate.
     */
    takerFeePercentOverride: z.number().min(0).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.minTradeSize > data.maxTradeSize) {
      ctx.addIssue({
        code: "custom",
        message: "minTradeSize must be less than maxTradeSize",
      });
    }
    if (data.minTargetVolume > data.maxTargetVolume) {
      ctx.addIssue({
        code: "custom",
        message: "minTargetVolume must be less than maxTargetVolume",
      });
    }
    if (data.maxTradeIntervalSeconds != null && data.maxTradeIntervalSeconds < data.minTradeIntervalSeconds) {
      ctx.addIssue({
        code: "custom",
        message: "maxTradeIntervalSeconds must be greater than or equal to minTradeIntervalSeconds",
      });
    }
    if (data.minHoldSeconds != null && data.maxHoldSeconds != null && data.maxHoldSeconds < data.minHoldSeconds) {
      ctx.addIssue({
        code: "custom",
        message: "maxHoldSeconds must be greater than or equal to minHoldSeconds",
      });
    }
    // A hold and order splitting can't both be on: a split+hold cycle would exceed the cycle-spacing margin.
    const hasHold = data.minHoldSeconds != null || data.maxHoldSeconds != null;
    if (hasHold && data.enableOrderSplitting === true) {
      ctx.addIssue({
        code: "custom",
        path: ["enableOrderSplitting"],
        message:
          "enableOrderSplitting cannot be true when a hold (minHoldSeconds/maxHoldSeconds) is set — a hold campaign places single orders",
      });
    }
  });
export type CampaignAutopilotConfigV2Data = z.infer<typeof CampaignAutopilotConfigV2DataSchema>;

/**
 * Versioned autopilot configuration for campaigns.
 * Uses discriminated union to allow different config structures per version.
 */
export const CampaignAutopilotConfigV1Schema = z.object({
  version: z.literal(1),
  data: CampaignAutopilotConfigV1DataSchema,
});
export type CampaignAutopilotConfigV1 = z.infer<typeof CampaignAutopilotConfigV1Schema>;

export const CampaignAutopilotConfigV2Schema = z.object({
  version: z.literal(2),
  data: CampaignAutopilotConfigV2DataSchema,
});
export type CampaignAutopilotConfigV2 = z.infer<typeof CampaignAutopilotConfigV2Schema>;

export const CampaignAutopilotConfigSchema = z.discriminatedUnion("version", [
  CampaignAutopilotConfigV1Schema,
  CampaignAutopilotConfigV2Schema,
]);
export type CampaignAutopilotConfig = z.infer<typeof CampaignAutopilotConfigSchema>;

export type PrizeBySlotReward = {
  quantity: number;
  volume: Decimal;
  reward: Decimal;
};

export type Phase = {
  start: Date;
  end: Date;
  maxVolume: number;
  id: number;
};

export type PhaseConfig = {
  duration: number;
  maxVolume: number;
};

export type EpochStep = {
  reward: number;
  proportionalReward: boolean; // if true, the reward is proportional to the volume of all rewarded users
  minVolume: number;
};

export const CampaignPartnerSocialName = {
  X: "x",
  DISCORD: "discord",
  TELEGRAM: "telegram",
  GITHUB: "github",
} as const;
export const CampaignPartnerSocialNameSchema = z.enum([
  CampaignPartnerSocialName.X,
  CampaignPartnerSocialName.DISCORD,
  CampaignPartnerSocialName.TELEGRAM,
  CampaignPartnerSocialName.GITHUB,
]);
export type CampaignPartnerSocialName = z.infer<typeof CampaignPartnerSocialNameSchema>;

export const CampaignPartnerSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
  description: z.string(),
  marketCap: z.string().optional().nullable(),
  allTimeLow: z.number().or(z.string()).optional().nullable(), // TODO: keep only string type once we have fully migrated the trade-to-earn module
  allTimeHigh: z.number().or(z.string()).optional().nullable(), // TODO: keep only string type once we have fully migrated the trade-to-earn module
  socials: z.array(
    z.object({
      name: CampaignPartnerSocialNameSchema,
      url: z.string(),
    }),
  ),
  websiteUrl: z.string().optional().nullable(),
  coinGeckoUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  icon: z.string().optional().nullable(),
});
export type CampaignPartner = z.infer<typeof CampaignPartnerSchema>;

const TradeToEarnCampaignPhaseSchema = z.object({
  start: z.coerce.date(),
  end: z.coerce.date(),
  maxVolume: z.number(),
  id: z.number(),
});
export type TradeToEarnCampaignPhase = z.infer<typeof TradeToEarnCampaignPhaseSchema>;

export const TradeToEarnCampaignEpochSchema = z.object({
  id: z.number(),
  epochGroupId: z.string(),
  /**
   * @remarks
   * The active epoch id is the index of the epoch in the campaign when we don't consider the paused epochs
   * @deprecated It should be removed once we have fully migrated the trade-to-earn module
   */
  activeEpochId: z.number(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  rule: TradingRuleSchema,
  /**
   * @remarks
   * Means the epoch is paused (partially or fully)
   * - Partially paused: the epoch started but was paused before it ended
   * - Fully paused: the epoch was paused before it started
   */
  isPaused: z.boolean().optional().nullable(),
  isPausedFrom: z.coerce.date().optional().nullable(),
  /**
   * @remarks
   * if the epoch is paused and the pause started before the current epoch start, then the epoch is considered fully paused
   * Otherwise, the epoch is considered partially paused (the pause started after the epoch start)
   */
  isFullyPaused: z.boolean().optional().nullable(),
  exchangeIds: z.array(z.string()).optional().nullable(),
  limitOrder: z.boolean().optional().nullable(),
  maxDefiAddressesByExchangeUserId: z.number().optional().nullable(),
  defiAddressBlockingPeriod: z.enum(["day", "epoch"]).optional().nullable(),
  /**
   * @deprecated It should be removed once we have fully migrated the trade-to-earn module
   */
  phases: z.array(TradeToEarnCampaignPhaseSchema).optional(),
  /**
   * @remarks
   * If set, only trade events from hub user having never traded before the given date will be considered for the leaderboard computation
   */
  onlyHubUserWithNoTradeEventsBefore: z.coerce.date().optional(),
  /**
   * @remarks
   * If true, only hub users who have never traded on this epoch's exchange(s) via Cede Hub
   * (in any other campaign or on the trade page) are eligible. Users who only traded within the
   * current campaign remain eligible. Per-exchange, scoped to this epoch's `exchangeIds`.
   */
  onlyHubUserWithNoTradeEventsOnExchange: z.boolean().optional().nullable(),
  publicationStatus: PublicationStatusSchema.optional().nullable(),
});
export type TradeToEarnCampaignEpoch = z.infer<typeof TradeToEarnCampaignEpochSchema>;

export const TradeToEarnCampaignEpochWithPrivateFieldsSchema = TradeToEarnCampaignEpochSchema.extend({
  expectedTradedVolume: z.number(),
});
export type TradeToEarnCampaignEpochWithPrivateFields = z.infer<typeof TradeToEarnCampaignEpochWithPrivateFieldsSchema>;

const TradeToEarnCampaignDescriptionSchema = z.object({
  id: z.number(),
  conditionalRenderingFromDate: z.coerce.date().nullable(),
  rule: z.string().nullable(),
  content: z.string(),
});
export type TradeToEarnCampaignDescription = z.infer<typeof TradeToEarnCampaignDescriptionSchema>;

export const TradeToEarnCampaignDefiAddressBlockingPeriod = {
  DAY: "day",
  EPOCH: "epoch",
} as const;
export const TradeToEarnCampaignDefiAddressBlockingPeriodSchema = z.enum([
  TradeToEarnCampaignDefiAddressBlockingPeriod.DAY,
  TradeToEarnCampaignDefiAddressBlockingPeriod.EPOCH,
]);
export type TradeToEarnCampaignDefiAddressBlockingPeriod = z.infer<
  typeof TradeToEarnCampaignDefiAddressBlockingPeriodSchema
>;

export const TradeToEarnCampaignEpochGroupSchema = z.object({
  id: z.union([z.number(), z.string()]),
  description: z.string().optional().nullable(),
  /**
   * @deprecated Use durationMs instead. Kept for backward compatibility with old campaigns.
   * Do not remove until data is migrated to durationMs.
   */
  duration: z.number().optional().nullable(),
  durationMs: z.number(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  /**
   * @remarks
   * Means the epoch is paused (partially or fully)
   * - Partially paused: the epoch started but was paused before it ended
   * - Fully paused: the epoch was paused before it started
   */
  isPaused: z.boolean().optional().nullable(),
  isPausedFrom: z.coerce.date().optional().nullable(),
  // TODO: add status field instead of computing it on the frontend each time
  production: z.boolean(),
  rule: TradingRuleSchema,
  exchangeIds: z.array(z.string()),
  limitOrder: z.boolean().optional().nullable(),
  maxDefiAddressesByExchangeUserId: z.number().optional().nullable(),
  defiAddressBlockingPeriod: TradeToEarnCampaignDefiAddressBlockingPeriodSchema.optional().nullable(),
  /**
   * @remarks
   * If set, only trade events from hub user having never traded before the given date will be considered for the leaderboard computation
   */
  onlyHubUserWithNoTradeEventsBefore: z.coerce.date().optional().nullable(),
  /**
   * @remarks
   * If true, only hub users who have never traded on this epoch group's exchange(s) via Cede Hub
   * (in any other campaign or on the trade page) are eligible. Users who only traded within the
   * current campaign remain eligible. Per-exchange, scoped to `exchangeIds`.
   */
  onlyHubUserWithNoTradeEventsOnExchange: z.boolean().optional().nullable(),
  publicationStatus: PublicationStatusSchema,
  autopilotConfig: CampaignAutopilotConfigSchema.optional().nullable(),
  tradingMode: CampaignTradingModeSchema.optional().nullable(),
  paperTradingConfig: PaperTradingConfigReadSchema.optional().nullable(),
});
export type TradeToEarnCampaignEpochGroup = z.infer<typeof TradeToEarnCampaignEpochGroupSchema>;

export const TradeToEarnCampaignEpochGroupWithPrivateFieldsSchema = TradeToEarnCampaignEpochGroupSchema.extend({
  expectedTradedVolume: z.number(),
});
export type TradeToEarnCampaignEpochGroupWithPrivateFields = z.infer<
  typeof TradeToEarnCampaignEpochGroupWithPrivateFieldsSchema
>;

export const TradeToEarnCampaignStatus = {
  LIVE: "live",
  FUTURE: "future",
  PAST: "past",
  PAUSED: "paused",
} as const;

export const TradeToEarnCampaignStatusSchema = z.enum([
  TradeToEarnCampaignStatus.LIVE,
  TradeToEarnCampaignStatus.FUTURE,
  TradeToEarnCampaignStatus.PAST,
  TradeToEarnCampaignStatus.PAUSED,
]);
export type TradeToEarnCampaignStatus = z.infer<typeof TradeToEarnCampaignStatusSchema>;

export const TradeToEarnCampaignRequireSocialsSchema = z.array(z.nativeEnum(SOCIAL_NETWORK));
export type TradeToEarnCampaignRequireSocials = z.infer<typeof TradeToEarnCampaignRequireSocialsSchema>;

/** One seated player of a Cede Arena duel. */
export const ArenaParticipantSchema = z.object({
  hubUserId: z.number().int().positive(),
  /** User-set Arena nickname (`HubUser.nickname`); `null` until set. */
  nickname: z.string().nullable(),
});
export type ArenaParticipant = z.infer<typeof ArenaParticipantSchema>;

/** One finishing position of a settled Cede Arena competition. */
export const ArenaPodiumEntrySchema = ArenaParticipantSchema.extend({
  /** 1 = winner. Mirrors `LeaderboardRecord.rank`. */
  rank: z.number().int().positive(),
});
export type ArenaPodiumEntry = z.infer<typeof ArenaPodiumEntrySchema>;

export const TradeToEarnCampaignSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
  title: z.string(),
  tokenSymbol: z.string(),
  /**
   * Market type for the campaign.
   * Used to specify whether trades should be on spot or perpetual markets.
   * Applies to exchanges that support both market types (like Hyperliquid).
   * Defaults to "spot" for backward compatibility.
   */
  marketType: CampaignMarketTypeSchema.optional().nullable(),
  prizePool: z.string().optional().nullable(),
  // Reward token symbol (multi-token rewards). Null when the campaign carries no
  // on-chain reward token yet (e.g. arena placeholder, or not backfilled).
  prizePoolToken: z.string().nullable(),
  currentEpochReward: z.string().optional().nullable(),
  updatedAt: z.coerce.date(),
  publishedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date(),
  production: z.boolean(),
  networkReward: z.array(z.string()).optional(),
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date(),
  visibility: z.enum(["public", "private"]).optional().nullable(),
  privateCampaignPartner: z.string().optional().nullable(),
  privateCampaignUtm: z.string().optional().nullable(),
  uniqueParticipationByUser: z.boolean().optional().nullable(),
  rewardToken: z.string().optional().nullable(),
  alertSelfTrading: z.boolean().optional().nullable(),
  slippageThreshold: z.number().optional().nullable(),
  /**
   * Per-rule trade-amount cap applied by the backend.
   * Defaults to `DEFAULT_MAX_TRADE_AMOUNT_USD` for every rule except PnL,
   * which uses `null` (unlimited). Not editable from the CMS anymore.
   */
  maxTradeAmountUSD: z.number().optional().nullable(),
  epochGroups: z.array(TradeToEarnCampaignEpochGroupSchema),
  descriptions: z.array(TradeToEarnCampaignDescriptionSchema),
  icon: z.string().optional().nullable(),
  privateCampaignUtms: z.array(z.string()).nullable().optional(),
  campaignPartners: z.array(CampaignPartnerSchema),
  rewardVaultAddress: z.string(),
  /**
   * Pool/market contract address for DEX campaigns (lowercase).
   * This is the address of the liquidity pool (e.g., AERO/USDC pool on Aerodrome),
   * NOT the token address. Used for secure validation against Mobula's marketAddress.
   */
  poolAddress: z.string().optional().nullable(),
  /**
   * Token contract address for DEX campaigns (lowercase).
   * This is the address of the campaign token (e.g., AERO token on Base).
   * Used for UI highlighting in the swap token selector.
   */
  dexTokenAddress: z.string().optional().nullable(),
  epochs: z.array(TradeToEarnCampaignEpochSchema),
  requiredSocialAccounts: TradeToEarnCampaignRequireSocialsSchema,
  currentEpoch: TradeToEarnCampaignEpochSchema.nullable(),
  nextEpoch: TradeToEarnCampaignEpochSchema.nullable(),
  status: TradeToEarnCampaignStatusSchema,
  isNew: z.boolean(),
  exchangeIds: z.array(z.string()),
  publicationStatus: PublicationStatusSchema,
  isParticipantWhitelisted: z.boolean().optional(),
  /**
   * Whether cashback is enabled for this campaign.
   * If false, no cashback will be awarded for trades in this campaign.
   * Defaults to true if not specified.
   */
  cashbackEnabled: z.boolean().optional().nullable(),
  /**
   * Override cashback rate for this campaign (0-1, e.g., 0.25 = 25%).
   * If null, uses the global cashback rate from config.
   */
  cashbackRateOverride: z.number().optional().nullable(),
  tradingMode: CampaignTradingModeSchema.optional().nullable(),
  /**
   * Whitelisted trading pairs for this campaign.
   * If set, only trades using these pairs will be eligible for rewards.
   * Empty array means no pair restrictions (all pairs allowed).
   */
  pairSymbols: z.array(tokenPairSymbolSchema).optional().nullable(),
  predictionEventIds: z.array(z.string()).optional().nullable(),
  /**
   * Spread threshold for alert system (percentage).
   * Alerts are triggered when spread exceeds this threshold.
   * Default: 2% if not configured.
   */
  spreadThresholdAlert: z.number().optional().nullable(),
  /**
   * Spread threshold for autopilot protection (percentage).
   * Autopilot cycles are skipped when spread exceeds this threshold.
   * Default: 2% if not configured.
   */
  autopilotSpreadThreshold: z.number().optional().nullable(),
  /**
   * Whether V2 autopilot start requests for this campaign must include a valid CAPTCHA token.
   * Evaluated at the campaign level only; epoch-group autopilot overrides do not affect enforcement.
   */
  autopilotCaptchaEnabled: z.boolean().optional().nullable(),
  /**
   * When true, the Hyperliquid referee-eligibility check is skipped for this campaign.
   * Used for PnL competitions where revenue is captured via Builder fees / Profit Participation
   * instead of referral fees, so already-referred wallets should not be blocked from joining.
   * Default: false (referee check enforced).
   */
  disableHyperliquidRefereeCheck: z.boolean().optional().nullable(),
  /**
   * Exchange IDs for which users must be affiliated (registered via CEX referral links)
   * to participate in the campaign. If not empty, the campaign is exclusive to users
   * who have an affiliated account on one of these exchanges.
   * Used to display "Exclusive" tag on campaign cards.
   */
  referralOnlyExchangeIds: z.array(ReferralSupportedExchangeIdSchema).optional().nullable(),
  /**
   * Versioned autopilot configuration for this campaign.
   * Contains settings that control autopilot behavior specific to this campaign.
   * - V1: Legacy config matching AutomaticStreakConfig structure
   * - V2: Simplified config with per-epoch volume targets
   */
  autopilotConfig: CampaignAutopilotConfigSchema.optional().nullable(),
  /**
   * Whether this campaign is sponsored by Kraken.
   * When true, trades on Kraken for this campaign are sent with a custom User-Agent header
   * so that Kraken can attribute the flow to Cede Hub.
   */
  sponsoredByKraken: z.boolean().optional().nullable(),
  /**
   * Cede Arena CMS flag — when true, the campaign appears on `/cede-arena`.
   * Discrete from the rule type so token-issuer PnL campaigns aren't
   * misclassified as competitions.
   */
  isArenaCompetition: z.boolean().optional().nullable(),
  /**
   * Matchmaking tier this duel plays under (its match proposal's tier). Null for
   * arena campaigns not created by matchmaking (blueprints, tournaments, private rooms).
   */
  arenaTier: ArenaEntryTierSchema.nullable().optional(),
  /**
   * Links this campaign to the Cede Arena competition blueprint that generated
   * it. Null for hand-made campaigns and arena competitions created outside a
   * blueprint.
   */
  blueprintId: z.string().optional().nullable(),
  isRestricted: z.boolean().optional(),
  isWhitelistedViewer: z.boolean().optional(),
  /**
   * Cede Arena per-campaign opt-in flag. When true (and the competition is
   * coming-soon), the Arena card shows an "Opt in" button that locks
   * `entryFees` from the user's virtual balance. Defaults to false.
   */
  userCanOptIn: z.boolean().optional().nullable(),
  /**
   * Entry fee for opting into this competition, as a decimal-USDT string
   * (e.g. "10"). Null when `userCanOptIn` is false.
   */
  entryFees: z.string().optional().nullable(),
  /**
   * Cede Arena per-campaign opt-in cap: the maximum number of users that may opt
   * into this competition. The Arena card locks the opt-in button once the live
   * opted-in count reaches it. Null/omitted means unlimited. Only meaningful when
   * `userCanOptIn` is true.
   */
  maxParticipants: z.number().int().positive().optional().nullable(),
  /**
   * Cede Arena guaranteed prize pool: when true, the prize pool at start is the
   * configured `prizePool` regardless of participant count (Cede tops up the gap).
   * Backend + CMS concern; defaults to false. Only meaningful when `userCanOptIn`
   * is true.
   */
  cedeFillPrizePool: z.boolean().optional().nullable(),
  /**
   * Cede Arena platform rake in basis points (0–5000 = 0%–50%) taken off this
   * competition's prize pool before the winner is paid. Snapshotted at creation,
   * locked for the competition's lifetime. Null on non-arena campaigns. The FE
   * derives the post-rake pool via `splitArenaRake(pool, arenaRakeBps,
   * arenaCreatorRakeBps)` so users see the net amount before opting in.
   */
  arenaRakeBps: z.number().int().optional().nullable(),
  /**
   * Cede Arena creator rake in basis points (0–2000), stacked on top of
   * `arenaRakeBps` and paid to the competition creator. Only set for private
   * competitions; null otherwise. The FE shows the platform + creator + net
   * breakdown before opt-in.
   */
  arenaCreatorRakeBps: z.number().int().optional().nullable(),
  /**
   * Cede Arena Spin & Go: true when this competition's prize pool is resized by a
   * drawn pot multiplier (`potFactor × collected buy-ins`). Only meaningful for
   * arena competitions; defaults to false.
   */
  isSpinAndGo: z.boolean().optional().nullable(),
  /** A tournament's single-match round: its empty whitelist admits nobody, and public listings skip it. */
  isArenaTournamentMatch: z.boolean().optional().nullable(),
  /**
   * The Spin & Go pot factor drawn + claimed for this competition at lock, as a
   * decimal string (pool = factor × collected). Null until claimed (and on non-spin
   * campaigns). Clients read it on rooms; a duel shows `spinAndGoBuyInMultiple`.
   */
  spinAndGoPotFactor: z.string().optional().nullable(),
  /**
   * The buy-in multiple a Spin & Go DUEL drew (pool = multiple × stake), e.g. "2".
   * Null until claimed, on non-spin campaigns, and on rooms (which keep pot factors).
   */
  spinAndGoBuyInMultiple: z.string().optional().nullable(),
  /**
   * Total buy-ins collected for this Spin & Go competition, as a decimal-USDT
   * string, stamped at lock. Surfaced so a client can compute the per-player stake
   * on the duel path, where `entryFees` is null (buy-ins live on the entries, not
   * the campaign). Null until stamped (and on non-spin campaigns).
   */
  spinAndGoCollectedUsdt: z.string().optional().nullable(),
  /**
   * `EXTERNAL` ⇒ an off-platform prize replaces the USDT payout: every rank is
   * credited 0 and the pot is house revenue.
   *
   * `prizePool` still carries the collected buy-ins, so clients MUST branch on this
   * field rather than inferring a non-cash prize from a zero pool.
   */
  arenaRewardKind: ArenaRewardKindSchema.optional().nullable(),
  /**
   * The prize as shown to players (e.g. "1 funded prop-firm account — $10,000"),
   * replacing the prize-pool amount everywhere. Set only when `arenaRewardKind`
   * is `EXTERNAL`.
   */
  arenaExternalRewardLabel: z.string().optional().nullable(),
  /**
   * Optional per-rank prize labels, positional: index `i` labels rank `i + 1`; an
   * empty entry leaves that rank unlabelled. Null ⇒ the headline label is 1st
   * place's prize and lower ranks win nothing. Read with
   * `resolveArenaExternalRewardSlotLabel`.
   */
  arenaExternalRewardSlotLabels: z.array(z.string()).optional().nullable(),
  /**
   * Per-viewer opt-in access flag, computed server-side for the authenticated
   * viewer when the competition gates opt-in behind an allowlist
   * (`exclusiveOptInParticipants`). `true` = this user may opt in, `false` = they
   * are blocked. `undefined`/`null` when the campaign doesn't gate opt-in or the
   * viewer is unauthenticated (access can't be decided yet). The raw allowlist
   * is never shipped to clients — only this boolean. The opt-in command remains
   * the authoritative gate.
   */
  canViewerOptIn: z.boolean().optional().nullable(),
  /**
   * Cede Arena — the `hubUserId` of the rank-1 winner of this competition's
   * latest ended PnL epoch (rank 1 with a non-zero reward). Null when the
   * competition has no settled winner yet (still running, or no rewarded
   * rank-1 record).
   */
  winnerHubUserId: z.number().int().positive().nullable().default(null),
  /**
   * Cede Arena — the winner's `HubUser.nickname`. Null when there's no winner
   * or the winner has no nickname set.
   */
  winnerNickname: z.string().nullable().default(null),
  /**
   * Cede Arena — true when the authenticated requester is this competition's
   * winner. Always false on the public (unauthenticated) endpoint.
   */
  didCurrentUserWin: z.boolean().default(false),
  /**
   * Cede Arena participant COUNT (never the identities — the allowlist stays
   * private). For a campaign with an authorized roster (auto-matched duels + private
   * rooms) this is `authorizedParticipants.length`; for opt-in campaigns it's the
   * opt-in count (0 for duels, which have no opt-in path). Undefined/null for
   * non-arena campaigns. Public so a battle page can show the count without the list.
   */
  participantCount: z.number().optional().nullable(),
  /**
   * Cede Arena DUEL roster — both matched players, so a surface can say who
   * faced whom without a leaderboard read.
   *
   * Populated ONLY for arena competitions whose roster has at most three entries,
   * i.e. 1v1 duels and 3-seat Spin & Go tables. A session's roster runs to hundreds and its surfaces need
   * only the leader (from the leaderboard) plus `participantCount`, so shipping
   * it would bloat every feed page for nothing. `undefined` for non-arena
   * campaigns, exactly like `participantCount`.
   *
   * Identities are already public for arena rosters — `PrivateCompetitionParticipantDto`
   * ships `{ hubUserId, nickname }` for every private room. Hub campaigns are
   * unaffected: the `isArenaCompetition` gate is the same one `participantCount` uses.
   *
   * Legacy wallet-string roster entries carry no hubUserId and are omitted;
   * `participantCount` still counts them, so it stays the truthful total.
   */
  arenaParticipants: z.array(ArenaParticipantSchema).optional(),
  /**
   * Cede Arena — the finishing order of a SETTLED competition's latest ended
   * epoch, best rank first, from the leaderboard.
   *
   * `winnerNickname` stays the authority when it is set: it comes from the
   * durable `HubUserCompetitionWin` table, and having the recorded winner and
   * the displayed winner be two different derivations is how this broke before.
   * This fills the gap where no win row exists — one is written ONLY for a
   * rank-1 with a non-zero reward, so a free duel never gets one, and neither
   * does any competition whose reward settled to zero.
   *
   * Duel-sized only, same bound as `arenaParticipants`.
   */
  arenaPodium: z.array(ArenaPodiumEntrySchema).optional(),
});
export type TradeToEarnCampaign = z.infer<typeof TradeToEarnCampaignSchema>;

export const TradeToEarnCampaignWithStatsSchema = TradeToEarnCampaignSchema.extend({
  stats: CampaignVolumeDataSchema,
});
export type TradeToEarnCampaignWithStats = z.infer<typeof TradeToEarnCampaignWithStatsSchema>;

export const TradeToEarnCampaignWithPrivateFieldsSchema = TradeToEarnCampaignSchema.extend({
  /**
   * Participant whitelist. Canonical persisted format is hubUserId numbers;
   * legacy rows may still carry lowercased 0x wallet strings until the data
   * migration runs, so readers must tolerate the mixed format.
   */
  authorizedParticipants: z.array(z.union([z.string(), z.number()])),
  /**
   * Cede Arena per-campaign opt-in allowlist (resolved hubUserId numbers; legacy
   * wallet strings tolerated, mirroring `authorizedParticipants`). Admin-only
   * field surfaced so the CMS can pre-fill the "Exclusive Access to Opt-In"
   * editor when editing a competition. Never exposed on the public
   * campaign/summary DTOs — only the computed `canViewerOptIn` boolean is.
   */
  exclusiveOptInParticipants: z.array(z.union([z.string(), z.number()])),
  epochGroups: z.array(TradeToEarnCampaignEpochGroupWithPrivateFieldsSchema),
  epochs: z.array(TradeToEarnCampaignEpochWithPrivateFieldsSchema),
  currentEpoch: TradeToEarnCampaignEpochWithPrivateFieldsSchema.nullable(),
  nextEpoch: TradeToEarnCampaignEpochWithPrivateFieldsSchema.nullable(),
  spreadThresholdAlert: z.number(), // Default: 2% - set in database
  autopilotSpreadThreshold: z.number(), // Default: 2% - set in database
  autopilotCaptchaEnabled: z.boolean(), // Default: false - set in database
  disableHyperliquidRefereeCheck: z.boolean(), // Default: false - set in database
  saleOwner: z.string().optional().nullable(),
  /**
   * Exchange IDs for which users must be affiliated (registered via CEX referral links)
   * to participate in the campaign. If the user's exchange account is not affiliated,
   * they will be blocked from participating.
   */
  referralOnlyExchangeIds: z.array(ReferralSupportedExchangeIdSchema).optional().nullable(),
  /**
   * Versioned autopilot configuration for this campaign (with private fields).
   */
  autopilotConfig: CampaignAutopilotConfigSchema.optional().nullable(),
  /**
   * Reward chain + ERC20 token (per-campaign). chainId defaults to Arbitrum (42161)
   * for legacy campaigns; token fields are populated from the CMS on-chain read.
   * Required: the DB column is NOT NULL (default 42161), so every producer can
   * and must populate it — making it required lets tsc catch any mapper that
   * drops it.
   */
  chainId: z.number(),
  rewardTokenAddress: z.string().optional().nullable(),
  rewardTokenSymbol: z.string().optional().nullable(),
  rewardTokenDecimals: z.number().optional().nullable(),
});
export type TradeToEarnCampaignWithPrivateFields = z.infer<typeof TradeToEarnCampaignWithPrivateFieldsSchema>;

export const TradeToEarnCampaignSummarySchema = TradeToEarnCampaignSchema.omit({
  campaignPartners: true,
  descriptions: true,
  epochs: true,
});
export type TradeToEarnCampaignSummary = z.infer<typeof TradeToEarnCampaignSummarySchema>;

/**
 * Per-consumer campaign projections.
 *
 * Each dashboard gets its own shape so one can gain or lose a field without touching the other,
 * and so neither pays for data it does not render. They deliberately do NOT reuse
 * `TradeToEarnCampaignSummary`: that type carries epoch and epoch-group relations, which grow
 * with campaign history and make the whole catalogue cost megabytes.
 *
 * `startedAt`/`endedAt` are nullable here because the columns are. The full campaign schema
 * declares them required, so a campaign without dates fails safeParse and silently disappears
 * from a list — which is why the consumers guard with `campaign.startedAt && ...`.
 */

/** Skywalker: campaign pickers and the live-campaign filters on the analytics dashboards. */
export const CampaignAnalyticsListItemSchema = z.object({
  name: z.string(),
  /**
   * Human-readable campaign name for pickers and filters — `name` is a slug ("psg-tte"). Optional
   * for the same reason as `rewardTokenSymbol`: Skywalker persists this list, so a required field
   * would reject every already-cached payload.
   */
  title: z.string().optional(),
  tokenSymbol: z.string(),
  /**
   * A view sums rewards across its campaigns, so they must all pay in the same token for that
   * total to mean anything. This is the traded token's counterpart, and the two differ routinely.
   *
   * Optional because Skywalker persists this list to localStorage with `staleTime: Infinity`:
   * a required field would fail `safeParse` against every already-cached payload, emptying the
   * campaign list until the cache happened to be evicted.
   */
  rewardTokenSymbol: z.string().nullable().optional(),
  /** The configured pool, which a case study quotes as the campaign's cost — not what was paid out. */
  prizePool: z.string().nullable().optional(),
  /** Discord id of the team member who owns the account; resolve names through `SALE_OWNERS`. */
  saleOwner: z.string().nullable().optional(),
  status: TradeToEarnCampaignStatusSchema,
  startedAt: z.coerce.date().nullable(),
  endedAt: z.coerce.date().nullable(),
});
export type CampaignAnalyticsListItem = z.infer<typeof CampaignAnalyticsListItemSchema>;

/** Rewards dashboard: the vault sidebar, which labels by date and sorts by creation. */
export const CampaignVaultListItemSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
  createdAt: z.coerce.date(),
  startedAt: z.coerce.date().nullable(),
  endedAt: z.coerce.date().nullable(),
});
export type CampaignVaultListItem = z.infer<typeof CampaignVaultListItemSchema>;

/**
 * Skywalker: the dashboard epoch picker and the per-epoch volume charts.
 *
 * The full campaign DTO attaches its epoch group's `rule` to every epoch, so a campaign with
 * hundreds of epochs serialises the same handful of rules hundreds of times. Skywalker only ever
 * reads the fields below, and `expectedTradedVolume` already resolves the rule fallback server-side.
 */
export const CampaignAnalyticsEpochSchema = z.object({
  id: z.number(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  exchangeIds: z.array(z.string()),
  expectedTradedVolume: z.number(),
});
export type CampaignAnalyticsEpoch = z.infer<typeof CampaignAnalyticsEpochSchema>;

/**
 * Skywalker: the generator inputs for a campaign's epochs. A group describes a cadence, so the
 * whole epoch list rebuilds from a handful of these — see `deriveCampaignEpochs`.
 */
export const CampaignAnalyticsEpochGroupSchema = z.object({
  start: z.coerce.date(),
  end: z.coerce.date(),
  /**
   * 0 means the group is a single epoch spanning its whole window. Constrained to a non-negative
   * integer because the derivation walks a cursor forward by this amount: a negative value would
   * step backwards and never reach the end, hanging the tab that expanded it.
   */
  durationMs: z.number().int().nonnegative(),
  exchangeIds: z.array(z.string()),
  expectedTradedVolume: z.number(),
});
export type CampaignAnalyticsEpochGroup = z.infer<typeof CampaignAnalyticsEpochGroupSchema>;

export const CampaignAnalyticsEpochGroupsSchema = z.object({
  name: z.string(),
  rewardToken: z.string().nullable(),
  groups: z.array(CampaignAnalyticsEpochGroupSchema),
  /** The campaign's stored epoch count; the client checks its derivation against it. */
  totalEpochs: z.number(),
});
export type CampaignAnalyticsEpochGroups = z.infer<typeof CampaignAnalyticsEpochGroupsSchema>;

export const CampaignAnalyticsEpochsSchema = z.object({
  name: z.string(),
  /** Rewards can be paid in a token other than the traded one; the dashboard labels totals with it. */
  rewardToken: z.string().nullable(),
  epochs: z.array(CampaignAnalyticsEpochSchema),
  /** Count of the campaign's epochs, independent of any page — a paging caller needs it to size its control. */
  total: z.number(),
});
export type CampaignAnalyticsEpochs = z.infer<typeof CampaignAnalyticsEpochsSchema>;

/**
 * Rewards dashboard: the vault detail header, its token balances and the Safe links. Deliberately
 * carries no epochs — the vault's epoch table pages against the analytics-epochs endpoint, so
 * shipping them here would put a campaign's whole epoch history behind a 25-row view.
 */
export const CampaignVaultDetailSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
  status: TradeToEarnCampaignStatusSchema,
  prizePoolToken: z.string().nullable(),
  rewardVaultAddress: z.string().nullable(),
  startedAt: z.coerce.date().nullable(),
  endedAt: z.coerce.date().nullable(),
});
export type CampaignVaultDetail = z.infer<typeof CampaignVaultDetailSchema>;

export const TradeToEarnCampaignSummaryWithPrivateFieldsSchema = TradeToEarnCampaignSummarySchema.extend({
  /** Mixed participant whitelist (hubUserId numbers + legacy wallet strings); see the campaign schema. */
  authorizedParticipants: z.array(z.union([z.string(), z.number()])),
  epochGroups: z.array(TradeToEarnCampaignEpochGroupWithPrivateFieldsSchema),
  currentEpoch: TradeToEarnCampaignEpochWithPrivateFieldsSchema.nullable(),
  nextEpoch: TradeToEarnCampaignEpochWithPrivateFieldsSchema.nullable(),
  spreadThresholdAlert: z.number(), // Default: 2% - set in database
  autopilotSpreadThreshold: z.number(), // Default: 2% - set in database
  autopilotCaptchaEnabled: z.boolean(), // Default: false - set in database
  disableHyperliquidRefereeCheck: z.boolean(), // Default: false - set in database
  referralOnlyExchangeIds: z.array(z.string()).optional().nullable(),
  autopilotConfig: CampaignAutopilotConfigSchema.optional().nullable(),
});
export type TradeToEarnCampaignSummaryWithPrivateFields = z.infer<
  typeof TradeToEarnCampaignSummaryWithPrivateFieldsSchema
>;

export const TradeToEarnCampaignWithoutEpochsSchema = TradeToEarnCampaignWithPrivateFieldsSchema.omit({
  epochs: true,
  currentEpoch: true,
  nextEpoch: true,
  epochGroups: true,
  campaignPartners: true,
  privateCampaignUtms: true,
});
export type TradeToEarnCampaignWithoutEpochs = z.infer<typeof TradeToEarnCampaignWithoutEpochsSchema>;

export const CampaignVisibility = {
  PUBLIC: "public",
  PRIVATE: "private",
} as const;
export const CampaignVisibilitySchema = z.enum([CampaignVisibility.PUBLIC, CampaignVisibility.PRIVATE]);
export type CampaignVisibility = z.infer<typeof CampaignVisibilitySchema>;

export const EpochDataSchema = z.object({
  epochId: z.number(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  exchangeIds: z.array(z.string()).optional().nullable(),
  expectedTradedVolume: z.number(),
});
export type EpochData = z.infer<typeof EpochDataSchema>;

export const LiveCampaignEpochsDataSchema = z.array(
  z.object({
    campaignName: z.string(),
    tokenSymbol: z.string(),
    epochs: z.array(EpochDataSchema),
  }),
);
export type LiveCampaignEpochsData = z.infer<typeof LiveCampaignEpochsDataSchema>;

export const CampaignLightWithCurrentEpochIdSchema = z.object({
  name: z.string(),
  currentEpochId: z.number().nullable(),
});
export type CampaignLightWithCurrentEpochId = z.infer<typeof CampaignLightWithCurrentEpochIdSchema>;

export const CampaignEpochIdentifierSchema = z.object({
  campaignName: z.string(),
  epochId: z.number(),
});
export type CampaignEpochIdentifier = z.infer<typeof CampaignEpochIdentifierSchema>;

/**
 * Represents the fields that can change on an epoch during a campaign update.
 */
export const EpochUpdateChangedField = {
  START: "start",
  END: "end",
  AUTOPILOT_CONFIG: "autopilotConfig",
  IS_PAUSED: "isPaused",
  RULE: "rule",
} as const;
export const EpochUpdateChangedFieldSchema = z.enum(["start", "end", "autopilotConfig", "isPaused", "rule"]);
export type EpochUpdateChangedField = z.infer<typeof EpochUpdateChangedFieldSchema>;

/**
 * Represents a detected change on a specific epoch during a campaign update.
 * - `changedFields`: list of fields that changed on an existing epoch
 * - `isRemoved`: true if the epoch no longer exists after the update
 */
export const EpochUpdateSchema = z.object({
  epochId: z.number(),
  changedFields: z.array(EpochUpdateChangedFieldSchema),
  isRemoved: z.boolean().optional(),
});
export type EpochUpdate = z.infer<typeof EpochUpdateSchema>;

export const TTECampaignSummaryResponseSchema = z.object({
  campaigns: z.array(TradeToEarnCampaignSummarySchema),
  cursor: z.string().nullable(),
  totalCount: z.number(),
  stats: CampaignsStatsResponseSchema,
});
export type TTECampaignSummaryResponse = z.infer<typeof TTECampaignSummaryResponseSchema>;

/**
 * Server-derived ERC20 reward-token metadata for a (chainId, address) pair.
 * Returned by the admin reward-token endpoints so the CMS prefills/auto-fills
 * `rewardTokenSymbol` / `rewardTokenDecimals` from the API's RPC instead of a
 * browser RPC call. `symbol` is the canonical reward-token symbol when the
 * address is a known reward token, otherwise the on-chain `symbol()`.
 */
export const RewardTokenMetadataResponseSchema = z.object({
  chainId: z.number().int().positive(),
  address: z.string().regex(/^0x[a-fA-F0-9]{40}$/),
  symbol: z.string().min(1),
  decimals: z.number().int().min(0),
});
export type RewardTokenMetadataResponse = z.infer<typeof RewardTokenMetadataResponseSchema>;
